import { createSlice } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { addFlashMessage } from '../flash/flashMessagesSlice'

const URL = 'renew_tasks'

// ==================== ASYNC THUNKS ====================

export const fetchRenewTasks = createApiAsyncThunk(
  'renewTasks/fetchAll',
  async (params = {}, { api, rejectWithValue }) => {
    try {
      const response = await api
        .url(URL)
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des tâches de renouvellement')
    }
  }
)

export const fetchRenewTask = createApiAsyncThunk(
  'renewTasks/fetchOne',
  async (taskId, { api, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}`)
        .get()
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateRenewTask = createApiAsyncThunk(
  'renewTasks/update',
  async ({ id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${id}`)
        .put({ renew_task: data })
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche mise à jour avec succès' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const completeRenewTask = createApiAsyncThunk(
  'renewTasks/complete',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/complete`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche complétée avec succès.' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const startRenewTask = createApiAsyncThunk(
  'renewTasks/start',
  async (taskId, { api, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/start`)
        .post()
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const cancelRenewTask = createApiAsyncThunk(
  'renewTasks/cancel',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/cancel`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche annulée' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const assignRenewTask = createApiAsyncThunk(
  'renewTasks/assign',
  async ({ taskId, userId }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/assign`)
        .post({ user_id: userId })
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche assignée avec succès' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteRenewTask = createApiAsyncThunk(
  'renewTasks/delete',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      await api
        .url(`${URL}/${taskId}`)
        .delete()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche supprimée avec succès' }))
      return { id: taskId }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// ==================== TASK TYPES ====================
// Importar configuración compartida
import { RENEW_TASK_TYPES, renewTaskTypeOptions as sharedRenewTaskTypeOptions } from './renewTaskTypes'

// Re-exportar para mantener compatibilidad con código existente
export const renewTaskTypes = RENEW_TASK_TYPES
export const renewTaskTypeOptions = sharedRenewTaskTypeOptions

// ==================== INITIAL STATE ====================

const initialState = {
  items: [],
  current: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    update: false,
    delete: false
  },
  requestStatus: {
    success: false,
    error: null
  },
  filters: {
    page: 1,
    limit: 20,
    status: '',
    task_type: '',
    bien_id: '',
    assigned_to_id: '',
    overdue: '',
    upcoming_days: ''
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  }
}

// ==================== SLICE ====================

const renewTasksSlice = createSlice({
  name: 'renewTasks',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
    },
    setCurrentTask: (state, action) => {
      state.current = action.payload
    },
    resetCurrentTask: (state) => {
      state.current = null
    },
    clearError: (state) => {
      state.requestStatus.error = null
    }
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder.addCase(fetchRenewTasks.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchRenewTasks.fulfilled, (state, action) => {
      state.loadingStatus.fetchList = false
      state.items = action.payload.tasks
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.requestStatus.success = true
    })
    builder.addCase(fetchRenewTasks.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // Fetch single task
    builder.addCase(fetchRenewTask.pending, (state) => {
      state.loadingStatus.fetchItem = true
    })
    builder.addCase(fetchRenewTask.fulfilled, (state, action) => {
      state.loadingStatus.fetchItem = false
      state.current = action.payload
    })
    builder.addCase(fetchRenewTask.rejected, (state, action) => {
      state.loadingStatus.fetchItem = false
      state.requestStatus.error = action.payload
    })

    // Update task
    builder.addCase(updateRenewTask.pending, (state) => {
      state.loadingStatus.update = true
    })
    builder.addCase(updateRenewTask.fulfilled, (state, action) => {
      state.loadingStatus.update = false
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      state.requestStatus.success = true
    })
    builder.addCase(updateRenewTask.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
    })

    // Complete task
    builder.addCase(completeRenewTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Start task
    builder.addCase(startRenewTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Cancel task
    builder.addCase(cancelRenewTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Assign task
    builder.addCase(assignRenewTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Delete task
    builder.addCase(deleteRenewTask.pending, (state) => {
      state.loadingStatus.delete = true
    })
    builder.addCase(deleteRenewTask.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      state.items = state.items.filter(t => t.id !== action.payload.id)
      state.requestStatus.success = true
    })
    builder.addCase(deleteRenewTask.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

export const {
  setFilters,
  resetFilters,
  setCurrentTask,
  resetCurrentTask,
  clearError
} = renewTasksSlice.actions

export default renewTasksSlice.reducer

